<template>
  <div class="onboarding-board">
    <EntraPageHeader
      title="Onboarding Board"
      :showTenantInfo="false"
      :showBreadcrumb="false"
      :showSearch="false"
      :showSavedViews="false"
      :showExport="false"
      :showRefresh="true"
      @refresh="loadBoard"
    />

    <div class="page-body">
      <div v-if="error" class="error-message" role="alert">{{ error }}</div>
      <div v-if="loading" class="loading-state">
        <span class="spinner" aria-hidden="true"></span> Loading board...
      </div>

      <template v-if="board">
        <router-link :to="'/presales/submissions/' + board.submissionId" class="back-link">
          <svg viewBox="0 0 20 20" fill="currentColor" class="back-icon">
            <path fill-rule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.61l4.04 3.79a.75.75 0 11-1.04 1.08l-5.25-4.92a.75.75 0 010-1.08l5.25-4.92a.75.75 0 111.04 1.08L5.61 9.25h10.64A.75.75 0 0117 10z" clip-rule="evenodd"/>
          </svg>
          Back to handover
        </router-link>

        <div class="card board-header">
          <div class="board-identity">
            <div class="avatar" aria-hidden="true">{{ customerInitials }}</div>
            <div>
              <h3 class="board-name">{{ board.customerName }}</h3>
              <div class="board-meta">
                <code class="ticket-chip">{{ board.dbTicket }}</code>
                <span class="muted">
                  Started {{ formatDate(board.createdAt) }}<template v-if="board.createdByName"> by {{ board.createdByName }}</template>
                </span>
              </div>
            </div>
          </div>
          <div class="board-progress">
            <span class="progress-label">{{ board.completedTasks }} / {{ board.totalTasks }} complete</span>
            <div class="progress-track">
              <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
            </div>
          </div>
        </div>

        <div v-for="section in sectionCards" :key="section.id" class="card section-card">
          <h4 class="section-title">{{ section.title }}</h4>
          <p v-if="section.note" class="section-note">{{ section.note }}</p>

          <div v-for="task in section.tasks" :key="task.id" class="task-block">
            <template v-if="task.subtasks">
              <div class="task-row task-group-header">
                <span class="task-title">{{ task.title }}</span>
                <span class="process-badge" :class="'process-' + task.process">{{ badgeLabel(task.process) }}</span>
              </div>
              <div class="subtask-list">
                <div
                  v-for="subtask in task.subtasks"
                  :key="subtask.id"
                  class="task-row"
                  :class="stateClass(subtask.id)"
                >
                  <button
                    type="button"
                    class="status-button"
                    :class="stateClass(subtask.id)"
                    :disabled="updating === subtask.id"
                    :title="nextStatusHint(subtask.id)"
                    @click="cycleStatus(subtask)"
                  >
                    <span class="status-dot"></span>
                    {{ statusLabel(subtask.id) }}
                  </button>
                  <div class="task-body">
                    <span class="task-title" :class="{ 'task-done': isResolved(subtask.id) }">{{ subtask.title }}</span>
                    <span v-if="subtask.note" class="task-note">{{ subtask.note }}</span>
                    <span v-if="attribution(subtask.id)" class="task-attribution">{{ attribution(subtask.id) }}</span>
                  </div>
                  <span class="process-badge" :class="'process-' + subtask.process">{{ badgeLabel(subtask.process) }}</span>
                </div>
              </div>
            </template>
            <div v-else class="task-row" :class="stateClass(task.id)">
              <button
                type="button"
                class="status-button"
                :class="stateClass(task.id)"
                :disabled="updating === task.id"
                :title="nextStatusHint(task.id)"
                @click="cycleStatus(task)"
              >
                <span class="status-dot"></span>
                {{ statusLabel(task.id) }}
              </button>
              <div class="task-body">
                <span class="task-title" :class="{ 'task-done': isResolved(task.id) }">{{ task.title }}</span>
                <span v-if="task.note" class="task-note">{{ task.note }}</span>
                <span v-if="attribution(task.id)" class="task-attribution">{{ attribution(task.id) }}</span>
              </div>
              <span class="process-badge" :class="'process-' + task.process">{{ badgeLabel(task.process) }}</span>
            </div>
          </div>
        </div>
      </template>

      <div v-if="!loading && !board && !error" class="empty-state">No onboarding board found for this id.</div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import { getOnboardingBoard, updateOnboardingTask } from '@/services/presales-api.js';

const STATUS_ORDER = ['pending', 'in_progress', 'completed'];
const STATUS_LABELS = {
  pending: 'Pending',
  in_progress: 'In progress',
  completed: 'Done',
  not_applicable: 'N/A'
};

export default {
  name: 'OnboardingBoard',
  components: { EntraPageHeader },
  data() {
    return {
      board: null,
      loading: false,
      error: null,
      updating: null
    };
  },
  computed: {
    boardId() {
      return this.$route.params.boardId || null;
    },
    progressPercent() {
      if (!this.board || this.board.totalTasks === 0) return 0;
      return Math.round((this.board.completedTasks / this.board.totalTasks) * 100);
    },
    customerInitials() {
      if (!this.board || !this.board.customerName) return '?';
      return this.board.customerName
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join('');
    },
    sectionCards() {
      return this.board ? this.board.sections : [];
    }
  },
  watch: {
    boardId() {
      this.loadBoard();
    }
  },
  mounted() {
    this.loadBoard();
  },
  methods: {
    async loadBoard() {
      if (!this.boardId) return;
      this.loading = true;
      this.error = null;
      try {
        this.board = await getOnboardingBoard(this.boardId);
      } catch (error) {
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },
    statusOf(taskId) {
      const state = this.board && this.board.states ? this.board.states[taskId] : null;
      return state ? state.status : 'pending';
    },
    statusLabel(taskId) {
      return STATUS_LABELS[this.statusOf(taskId)] || 'Pending';
    },
    stateClass(taskId) {
      return 'state-' + this.statusOf(taskId).replace(/_/g, '-');
    },
    isResolved(taskId) {
      const status = this.statusOf(taskId);
      return status === 'completed' || status === 'not_applicable';
    },
    attribution(taskId) {
      const state = this.board && this.board.states ? this.board.states[taskId] : null;
      if (!state || !state.completedByName || !state.completedAt) return null;
      return 'Completed by ' + state.completedByName + ' on ' + this.formatDate(state.completedAt);
    },
    nextStatusHint(taskId) {
      const status = this.statusOf(taskId);
      if (status === 'pending') return 'Mark in progress';
      if (status === 'in_progress') return 'Mark done';
      if (status === 'completed') return 'Reset to pending';
      return 'Reset to pending';
    },
    async cycleStatus(task) {
      const current = this.statusOf(task.id);
      const next = current === 'not_applicable'
        ? 'pending'
        : STATUS_ORDER[(STATUS_ORDER.indexOf(current) + 1) % STATUS_ORDER.length];
      this.updating = task.id;
      this.error = null;
      try {
        this.board = await updateOnboardingTask(this.boardId, task.id, next);
      } catch (error) {
        this.error = error.message;
      } finally {
        this.updating = null;
      }
    },
    badgeLabel(process) {
      if (process === 'automated') return 'Automated';
      if (process === 'automated-script') return 'Automated (script)';
      return 'Manual';
    },
    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleString();
    }
  }
};
</script>

<style scoped>
.onboarding-board {
  min-height: 100vh;
  background: #f8fafc;
}

.page-body {
  padding: 1.5rem 2rem 3rem;
  max-width: 1100px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: #64748b;
  font-size: 0.8125rem;
  text-decoration: none;
  margin-bottom: 0.875rem;
}

.back-link:hover {
  color: #4f46e5;
}

.back-icon {
  width: 0.9rem;
  height: 0.9rem;
}

.card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  padding: 1.5rem;
  margin-bottom: 1.25rem;
}

.board-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.board-identity {
  display: flex;
  align-items: center;
  gap: 0.875rem;
}

.avatar {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 999px;
  background: #4f46e5;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.95rem;
}

.board-name {
  margin: 0;
  font-size: 1.125rem;
  color: #0f172a;
}

.board-meta {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-top: 0.25rem;
  font-size: 0.8125rem;
}

.ticket-chip {
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  color: #3730a3;
  border-radius: 6px;
  padding: 0.1rem 0.45rem;
  font-size: 0.75rem;
  font-weight: 600;
}

.muted {
  color: #64748b;
}

.board-progress {
  min-width: 220px;
}

.progress-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.progress-track {
  margin-top: 0.4rem;
  height: 8px;
  background: #eef2ff;
  border-radius: 999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #4f46e5;
  border-radius: 999px;
  transition: width 250ms ease;
}

.section-card {
  padding: 1.25rem 1.5rem;
}

.section-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
}

.section-note {
  margin: 0.375rem 0 0;
  font-size: 0.8125rem;
  color: #b45309;
}

.task-block {
  margin-top: 0.875rem;
}

.task-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
}

.task-row + .task-row {
  margin-top: 0.5rem;
}

.task-row.state-in-progress {
  border-color: #fde68a;
  background: #fffbeb;
}

.task-row.state-completed {
  border-color: #bbf7d0;
  background: #f0fdf4;
}

.task-row.state-not-applicable {
  opacity: 0.6;
}

.task-group-header {
  border: none;
  background: transparent;
  padding: 0.25rem 0.75rem;
}

.task-group-header .task-title {
  font-weight: 700;
}

.subtask-list {
  margin: 0.375rem 0 0 1.25rem;
}

.status-button {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #475569;
  border-radius: 999px;
  padding: 0.2rem 0.7rem;
  font-size: 0.72rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 150ms ease, background 150ms ease, color 150ms ease;
}

.status-button:disabled {
  opacity: 0.6;
  cursor: wait;
}

.status-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: #cbd5e1;
}

.status-button.state-in-progress {
  border-color: #f59e0b;
  color: #b45309;
}

.status-button.state-in-progress .status-dot {
  background: #f59e0b;
}

.status-button.state-completed {
  border-color: #16a34a;
  color: #15803d;
}

.status-button.state-completed .status-dot {
  background: #16a34a;
}

.status-button.state-not-applicable {
  text-decoration: line-through;
}

.task-body {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}

.task-title {
  font-size: 0.875rem;
  color: #1e293b;
}

.task-done {
  text-decoration: line-through;
  color: #64748b;
}

.task-note {
  font-size: 0.78rem;
  color: #64748b;
  font-style: italic;
}

.task-attribution {
  font-size: 0.72rem;
  color: #16a34a;
}

.process-badge {
  flex: 0 0 auto;
  margin-left: auto;
  border-radius: 999px;
  padding: 0.15rem 0.6rem;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.process-automated,
.process-automated-script {
  background: #ecfeff;
  border-color: #a5f3fc;
  color: #0e7490;
}

.loading-state,
.empty-state {
  color: #64748b;
  padding: 2rem 0;
}

.spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid #c7d2fe;
  border-top-color: #4f46e5;
  border-radius: 999px;
  animation: spin 600ms linear infinite;
  margin-right: 0.4rem;
  vertical-align: -2px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1.25rem;
  font-size: 0.875rem;
}
</style>
